// src/components/Data/DataList.js
import React, { useState, useEffect, useCallback } from "react";
import {
  fetchData,
  addItem,
  updateItem,
  deleteItem,
  fetchItemById,
} from "../../services/api";
import { useAppContext } from "../../context/AppContext";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import Modal from "../ui/modal";
import { Edit, Trash2, Plus, Search, Image, Store, Tag } from "lucide-react";
import { Pagination } from "../ui/pagination";
import { Label } from "../ui/label";
import { Skeleton } from "../ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";

function DataList() {
  // Use local state to prevent the infinite loop
  const [localData, setLocalData] = useState([]);
  const [localLoading, setLocalLoading] = useState(true);
  const [localError, setLocalError] = useState(null);
  const [localPage, setLocalPage] = useState(1);
  const [localTotalPages, setLocalTotalPages] = useState(1);
  const [localSearchTerm, setLocalSearchTerm] = useState("");

  const { actions } = useAppContext();

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [formData, setFormData] = useState({
    shopName: "",
    itemName: "",
    image: null,
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);

  // Add state to track loading and API failure status
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [apiHasFailed, setApiHasFailed] = useState(false);

  const loadData = useCallback(async () => {
    // If we're already loading data or API has previously failed, return early
    if (isLoadingData || apiHasFailed) return;

    try {
      setIsLoadingData(true);
      setLocalLoading(true);

      const result = await fetchData(localPage, 10, localSearchTerm);

      setLocalData(result.items || []);
      setLocalTotalPages(result.totalPages || 1);
      setLocalLoading(false);
      // Reset API failure flag on successful API call
      setApiHasFailed(false);
    } catch (err) {
      setLocalError(err.message || "Failed to fetch data");
      setLocalLoading(false);
      // Set API failure flag to prevent repeat calls
      setApiHasFailed(true);
      actions.setNotification(err.message || "Failed to fetch data", "error");
    } finally {
      setIsLoadingData(false);
    }
  }, [localPage, localSearchTerm, actions, isLoadingData, apiHasFailed]);

  // FIX: Use a different useEffect pattern to prevent the infinite loop
  useEffect(() => {
    // This will only run on component mount and when dependencies change
    loadData();
  }, [localPage, localSearchTerm]); // Removed loadData from dependencies to prevent infinite loop

  const handleSearch = (e) => {
    e.preventDefault();
    setLocalPage(1);
  };

  const handlePageChange = (newPage) => {
    setLocalPage(newPage);
  };

  const handleSearchTermChange = (e) => {
    setLocalSearchTerm(e.target.value);
  };

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "image" && files && files[0]) {
      setFormData((prev) => ({
        ...prev,
        [name]: files[0],
      }));

      // Create image preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(files[0]);
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const resetForm = () => {
    setFormData({
      shopName: "",
      itemName: "",
      image: null,
    });
    setImagePreview(null);
    setCurrentItem(null);
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("shopName", formData.shopName);
      formDataToSend.append("itemName", formData.itemName);
      formDataToSend.append("image", formData.image);

      await addItem(formDataToSend);
      setIsAddModalOpen(false);
      resetForm();
      loadData();
      actions.setNotification("Item added successfully", "success");
    } catch (err) {
      actions.setNotification(err.message || "Failed to add item", "error");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEditClick = async (item) => {
    try {
      const fullItem = await fetchItemById(item.id);
      setCurrentItem(fullItem);
      setFormData({
        shopName: fullItem.shopName,
        itemName: fullItem.itemName,
        image: null,
      });
      setImagePreview(fullItem.imageFile);
      setIsEditModalOpen(true);
    } catch (err) {
      actions.setNotification(
        err.message || "Failed to fetch item details",
        "error"
      );
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("shopName", formData.shopName);
      formDataToSend.append("itemName", formData.itemName);
      if (formData.image) {
        formDataToSend.append("image", formData.image);
      }

      await updateItem(currentItem.id, formDataToSend);
      setIsEditModalOpen(false);
      resetForm();
      loadData();
      actions.setNotification("Item updated successfully", "success");
    } catch (err) {
      actions.setNotification(err.message || "Failed to update item", "error");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDeleteClick = (item) => {
    setCurrentItem(item);
    setIsDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      await deleteItem(currentItem.id);
      setIsDeleteDialogOpen(false);
      loadData();
      actions.setNotification("Item deleted successfully", "success");
    } catch (err) {
      actions.setNotification(err.message || "Failed to delete item", "error");
    }
  };

  // For development/testing when the API is not available
  const mockData = [
    {
      id: 1,
      shopName: "Coffee Shop",
      itemName: "Espresso Machine",
      imageFile: "https://via.placeholder.com/150",
    },
    {
      id: 2,
      shopName: "Book Store",
      itemName: "Bestseller Collection",
      imageFile: "https://via.placeholder.com/150",
    },
    {
      id: 3,
      shopName: "Tech Store",
      itemName: "Laptop",
      imageFile: "https://via.placeholder.com/150",
    },
  ];

  // Use mock data if there's an error or insufficient resources
  const displayData = localData;

  // Render loading skeletons
  if (localLoading && displayData.length === 0) {
    return (
      <div className="space-y-4">
        <div className="flex flex-col md:flex-row justify-between mb-6 space-y-4 md:space-y-0 md:space-x-4">
          <div className="w-full md:w-2/3">
            <Skeleton className="h-10 w-full" />
          </div>
          <Skeleton className="h-10 w-full md:w-1/3" />
        </div>

        <div className="overflow-x-auto border rounded-lg">
          <div className="min-w-full divide-y">
            <div className="bg-gray-50">
              <div className="grid grid-cols-4 px-6 py-3">
                <Skeleton className="h-6 w-20" />
                <Skeleton className="h-6 w-20" />
                <Skeleton className="h-6 w-20" />
                <Skeleton className="h-6 w-20" />
              </div>
            </div>
            <div className="bg-white divide-y">
              {[1, 2, 3, 4, 5].map((item) => (
                <div key={item} className="grid grid-cols-4 px-6 py-4">
                  <Skeleton className="h-16 w-16 rounded-full" />
                  <Skeleton className="h-6 w-full" />
                  <Skeleton className="h-6 w-full" />
                  <Skeleton className="h-10 w-28" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row justify-between mb-6 space-y-4 md:space-y-0 md:space-x-4">
        <form onSubmit={handleSearch} className="flex-grow flex space-x-2">
          <div className="relative flex-grow">
            <Input
              type="text"
              placeholder="Search items..."
              value={localSearchTerm}
              onChange={handleSearchTermChange}
              className="w-full pl-10 pr-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
          </div>
          <Button
            type="submit"
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            Search
          </Button>
        </form>
        <Button
          onClick={() => {
            resetForm();
            setIsAddModalOpen(true);
          }}
          className="bg-green-600 hover:bg-green-700 text-white flex items-center"
        >
          <Plus className="w-5 h-5 mr-2" /> Add New Item
        </Button>
      </div>

      {localError && (
        <div className="p-4 text-red-700 bg-red-100 rounded-md">
          {localError}
          <p className="mt-2 text-sm">Using demo data for preview</p>
          <div className="mt-2">
            <Button
              onClick={() => {
                setApiHasFailed(false);
                loadData();
              }}
              variant="outline"
              size="sm"
              className="text-red-600 border-red-300 hover:bg-red-50"
            >
              Retry Connection
            </Button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto border rounded-lg shadow-sm bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead className="w-24 font-semibold">Image</TableHead>
              <TableHead className="font-semibold">Shop Name</TableHead>
              <TableHead className="font-semibold">Item Name</TableHead>
              <TableHead className="w-32 text-right font-semibold">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {displayData.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={4}
                  className="text-center py-8 text-gray-500"
                >
                  No items found. Try adding a new item or changing your search
                  criteria.
                </TableCell>
              </TableRow>
            ) : (
              displayData.map((item) => (
                <TableRow key={item.id} className="hover:bg-gray-50">
                  <TableCell>
                    <div className="relative w-16 h-16 rounded-full overflow-hidden border border-gray-200">
                      <img
                        src={item.imageFile}
                        alt={item.itemName}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = "https://via.placeholder.com/150";
                        }}
                      />
                    </div>
                  </TableCell>
                  <TableCell className="font-medium">{item.shopName}</TableCell>
                  <TableCell>{item.itemName}</TableCell>
                  <TableCell>
                    <div className="flex justify-end space-x-2">
                      <Button
                        onClick={() => handleEditClick(item)}
                        variant="outline"
                        size="sm"
                        className="flex items-center border-gray-300 hover:bg-gray-100"
                      >
                        <Edit className="w-4 h-4 mr-1 text-blue-600" />
                        <span className="hidden sm:inline">Edit</span>
                      </Button>
                      <Button
                        onClick={() => handleDeleteClick(item)}
                        variant="outline"
                        size="sm"
                        className="flex items-center border-gray-300 hover:bg-red-50 text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        <span className="hidden sm:inline">Delete</span>
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {localTotalPages > 1 && (
        <div className="mt-4 flex justify-center">
          <Pagination
            currentPage={localPage}
            totalPages={localTotalPages}
            onPageChange={handlePageChange}
          />
        </div>
      )}

      {/* Add Item Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        title="Add New Item"
      >
        <form onSubmit={handleAdd} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="add-shop-name">Shop Name</Label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Store className="h-5 w-5 text-gray-400" />
              </div>
              <Input
                id="add-shop-name"
                name="shopName"
                placeholder="Enter shop name"
                value={formData.shopName}
                onChange={handleInputChange}
                className="pl-10"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="add-item-name">Item Name</Label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Tag className="h-5 w-5 text-gray-400" />
              </div>
              <Input
                id="add-item-name"
                name="itemName"
                placeholder="Enter item name"
                value={formData.itemName}
                onChange={handleInputChange}
                className="pl-10"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="add-image">Item Image</Label>
            <div className="flex items-center space-x-4">
              {imagePreview && (
                <div className="w-20 h-20 rounded-md overflow-hidden border border-gray-200">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div className="flex-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Image className="h-5 w-5 text-gray-400" />
                </div>
                <Input
                  id="add-image"
                  name="image"
                  type="file"
                  accept="image/*"
                  onChange={handleInputChange}
                  className="pl-10"
                  required
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsAddModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-blue-600 hover:bg-blue-700"
              disabled={submitLoading}
            >
              {submitLoading ? "Adding..." : "Add Item"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Edit Item Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Edit Item"
      >
        <form onSubmit={handleEdit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-shop-name">Shop Name</Label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Store className="h-5 w-5 text-gray-400" />
              </div>
              <Input
                id="edit-shop-name"
                name="shopName"
                placeholder="Enter shop name"
                value={formData.shopName}
                onChange={handleInputChange}
                className="pl-10"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-item-name">Item Name</Label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Tag className="h-5 w-5 text-gray-400" />
              </div>
              <Input
                id="edit-item-name"
                name="itemName"
                placeholder="Enter item name"
                value={formData.itemName}
                onChange={handleInputChange}
                className="pl-10"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-image">Item Image</Label>
            <div className="flex items-center space-x-4">
              {imagePreview && (
                <div className="w-20 h-20 rounded-md overflow-hidden border border-gray-200">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div className="flex-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Image className="h-5 w-5 text-gray-400" />
                </div>
                <Input
                  id="edit-image"
                  name="image"
                  type="file"
                  accept="image/*"
                  onChange={handleInputChange}
                  className="pl-10"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Leave empty to keep current image
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsEditModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-blue-600 hover:bg-blue-700"
              disabled={submitLoading}
            >
              {submitLoading ? "Updating..." : "Update Item"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the item "{currentItem?.itemName}".
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default DataList;
